import type { ProposalDetail } from '../core/types';
import { isExecutionError } from './helpers';

export type ExecutionOutcome =
  | { status: 'success' }
  | { status: 'failure'; message: string };

/**
 * Classifies a resolved proposal's execution outcome. Ported verbatim from
 * the inline logic in StickyFooter's polling `onComplete` handler.
 */
export function classifyExecutionOutcome(
  proposal: ProposalDetail
): ExecutionOutcome {
  const execResult = proposal.execution_result;

  if (execResult && isExecutionError(execResult)) {
    const completed = execResult.actions_completed;
    const total = execResult.total_actions;
    let message = execResult.error;
    if (
      typeof completed === 'number' &&
      typeof total === 'number' &&
      completed > 0
    ) {
      message += ` (${completed} of ${total} actions completed). You can safely retry – completed actions won't be duplicated.`;
    } else if (proposal.resolution === 'pending') {
      message += '. You can retry by clicking Approve again.';
    }
    return { status: 'failure', message };
  }

  if (
    execResult &&
    typeof execResult === 'object' &&
    'failed' in execResult &&
    (execResult as { failed: number }).failed > 0
  ) {
    const failedAction = proposal.actions.find(a => {
      const s = a.execution_result?.status;
      return s === 'error' || s === 'failure' || s === 'indeterminate';
    });
    const message = `Execution failed: ${failedAction?.execution_result?.error || 'Action execution failed'}`;
    return { status: 'failure', message };
  }

  return { status: 'success' };
}
